import React from 'react';
import { Icon, Popover } from '@brighterion/br-ui';
import clsx from 'clsx';
import { Conclusion } from 'Common/enums';

import styles from './FormattedConclusion.css';

type Score = number | undefined;

type Props = {
    value: string;
    score: Score;
};

function getIconName(value: string, score: Score) {
    if (value === 'Accept') {
        return 'accept';
    }
    if (value === 'Reject') {
        return 'reject';
    }
    if (value === 'Review') {
        return 'table-decision';
    }
    if (typeof score === 'number') {
        return score >= 0 ? 'score-increase' : 'score-decrease';
    }
    return '';
}

const renderRows = (icon: JSX.Element, value: string) => {
    return (
        <div className={styles.conclusion}>
            {icon}
            <span className={styles.conclusionValues}>{value}</span>
        </div>
    );
};

const replaceStringWithScore = (value: string, score: Score) => {
    if (value?.includes('[score]')) {
        return value.replace(/\[score\]/g, `${score}`);
    }
    return value;
};

const FormattedConclusion = ({ value, score }: Props) => {
    const status: string = value ? value.split(' ')[0] : '';
    const iconName = getIconName(status, score);

    const isReadOnly = status !== 'Accept' && status !== 'Reject' && status !== 'Review' && score === 0;

    const icon = (
        <Icon
            name={iconName}
            size='md'
            className={clsx(styles.icon, isReadOnly && styles.grayIcon)}
            aria-label={iconName}
        />
    );

    if (
        value === Conclusion.ACCEPT ||
        value === Conclusion.REJECT ||
        value === Conclusion.REVIEW ||
        value === Conclusion.SEE_RULE_DETAILS
    ) {
        return renderRows(icon, value);
    }

    if (value === Conclusion.UPDATE_SCORE_BY_SCORE || value === Conclusion.SCORE_SCORE) {
        return renderRows(icon, replaceStringWithScore(value, score));
    }

    return (
        <Popover
            arrowClassName={styles.popperArrowStyle}
            content={replaceStringWithScore(value, score)}
            contentClassName={styles.popOverContentStyle}
            position='top-start'
            showArrow
            showOnHover
            size='small'
        >
            {renderRows(icon, replaceStringWithScore(value, score))}
        </Popover>
    );
};

export default FormattedConclusion;
